import classNames from "classnames";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DoubleLineMenu } from "components/icons/DoubleLineMenu";
import { SmallDoubleArrowLine } from "components/icons/SmallDoubleArrowLine";
import { SmallSingleArrowLine } from "components/icons/SmallSingleArrowLine";
import {
  TICKET_PRIORITY_OPTIONS,
  TICKET_PRIORITY_TYPES,
  TICKET_PRIORITY_TYPES_DISPLAY,
  TICKET_PRIORITY_TYPES_TYPE,
} from "constants/ticket";
import { Formik } from "formik";
import { useToast } from "hooks/useToast";
import React from "react";
import {
  components,
  ControlProps,
  MultiValue,
  SingleValue,
} from "react-select";
import { useUpdateChatTicketMutation } from "store/apis/chatTicket";
import { BackendError } from "types/utils/error";

type TicketPriorityProps = {
  priority: TICKET_PRIORITY_TYPES_TYPE | null;
  ticketId: string;
};

export const PriorityComboboxControl = ({
  children,
  ...props
}: ControlProps<OptionData>) => {
  const {
    selectProps: { value },
  } = props;

  return (
    <components.Control
      {...props}
      className={classNames(
        "t-px-2 t-border t-border-solid hover:t-border-neutral-10 hover:t-bg-surface-grey t-rounded !t-cursor-pointer",
        {
          " t-border-neutral-0 t-bg-surface-lighter-grey": Boolean(!value),
          "t-border-surface-transparent": Boolean(value),
        }
      )}
    >
      {children}
    </components.Control>
  );
};

const PRIORITY_ICON_CONFIG = {
  LOW: {
    icon: SmallSingleArrowLine,
  },
  MEDIUM: {
    icon: DoubleLineMenu,
  },
  HIGH: {
    icon: SmallSingleArrowLine,
  },
  CRITICAL: {
    icon: SmallDoubleArrowLine,
  },
} as const;

export const TicketPriority = ({ priority, ticketId }: TicketPriorityProps) => {
  const { alertToast, successToast } = useToast();
  const [updateChatTicket] = useUpdateChatTicketMutation();

  const renderIcon = (
    priority: TICKET_PRIORITY_TYPES_TYPE
  ): React.ReactNode => {
    if (!priority || !(priority in PRIORITY_ICON_CONFIG)) {
      return "-";
    }

    const config = PRIORITY_ICON_CONFIG[priority];
    const IconComponent = config.icon;

    return (
      <IconComponent
        className={classNames({
          "t-text-blue t-rotate-180": priority === TICKET_PRIORITY_TYPES.LOW,
          "t-text-yellow-70": priority === TICKET_PRIORITY_TYPES.MEDIUM,
          "t-text-red":
            priority === TICKET_PRIORITY_TYPES.HIGH ||
            priority === TICKET_PRIORITY_TYPES.CRITICAL,
        })}
      />
    );
  };

  const getValue = (priority: TICKET_PRIORITY_TYPES_TYPE) => {
    return (
      <div className="t-flex t-gap-1.5 t-items-center">
        {renderIcon(priority)}
        <div className="t-text-body-sm t-text-text-60">
          {priority ? TICKET_PRIORITY_TYPES_DISPLAY[priority] : "-"}
        </div>
      </div>
    );
  };

  const handlePriorityChange = async (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    try {
      if (!option || option instanceof Array) {
        return;
      }

      const selectedPriority = option.value as TICKET_PRIORITY_TYPES_TYPE;

      await updateChatTicket({
        priority: selectedPriority,
        ticket_id: ticketId,
      }).unwrap();

      successToast({ message: "Priority updated successfully" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        priority: priority || "",
      }}
      onSubmit={() => {}}
    >
      {({ isSubmitting, values }) => {
        const selectedLabel = getValue(
          values.priority as TICKET_PRIORITY_TYPES_TYPE
        );
        return (
          <div
            className="t-combobox-group t-select-none"
            onClick={(e) => e.stopPropagation()}
          >
            <Combobox
              label=""
              menuPlacement="bottom"
              menuPortalTarget={document.body}
              withForm
              components={{
                Control: PriorityComboboxControl,
                ClearIndicator: () => null,
              }}
              name="priority"
              options={TICKET_PRIORITY_OPTIONS}
              isLoading={isSubmitting}
              value={{
                label: selectedLabel,
                value: values.priority || "",
              }}
              onChange={handlePriorityChange}
            />
          </div>
        );
      }}
    </Formik>
  );
};
